import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, Save, RefreshCw, Undo2, Volume2, Loader } from 'lucide-react';
import { API_URL } from '../constants';

const MeditationsTab = () => {
    const [meditations, setMeditations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [editingContent, setEditingContent] = useState(null);
    const [originalContent, setOriginalContent] = useState(null); // For undo feature
    const [saving, setSaving] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [generatingAudio, setGeneratingAudio] = useState(null); // meditation_id being generated
    const [cacheStatus, setCacheStatus] = useState({}); // {meditation_id: {cached, total, percentage}}

    const fetchMeditations = async () => {
        try {
            const res = await fetch(`${API_URL}/api/meditations?active_only=false`);
            const data = await res.json();
            setMeditations(data);
            
            // Fetch cache status for guided meditations
            const guidedMeds = data.filter(m => m.content_type === 'guided_meditation');
            for (const med of guidedMeds) {
                try {
                    const statusRes = await fetch(`${API_URL}/api/audio-cache/status/${med.content_id}`);
                    if (statusRes.ok) {
                        const status = await statusRes.json();
                        setCacheStatus(prev => ({
                            ...prev,
                            [med.content_id]: status
                        }));
                    }
                } catch (e) {
                    // Ignore cache status errors
                }
            }
        } catch (err) {
            console.error('Error fetching meditations:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMeditations();
    }, []);

    // Track changes
    useEffect(() => {
        if (editingContent && originalContent) {
            const changed = JSON.stringify(editingContent) !== JSON.stringify(originalContent);
            setHasChanges(changed);
        }
    }, [editingContent, originalContent]);

    const handleSeedData = async () => {
        setSeeding(true);
        try {
            const res = await fetch(`${API_URL}/api/meditations/seed`, { method: 'POST' });
            const data = await res.json();
            alert(`${data.message}\nInserted: ${data.inserted}, Updated: ${data.updated}`);
            fetchMeditations();
        } catch (err) {
            alert('Error seeding data: ' + err.message);
        } finally {
            setSeeding(false);
        }
    };

    const handlePreGenerateAudio = async (meditationId) => {
        setGeneratingAudio(meditationId);
        try {
            const res = await fetch(`${API_URL}/api/audio-cache/pre-generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ meditation_id: meditationId })
            });
            const data = await res.json();
            alert(`${data.message}\nThis will run in the background. Refresh to see progress.`);
            
            // Poll for status updates
            setTimeout(() => fetchMeditations(), 5000);
        } catch (err) {
            alert('Error: ' + err.message);
        } finally {
            setGeneratingAudio(null);
        }
    };

    const handlePreGenerateAll = async () => {
        setGeneratingAudio('all');
        try {
            const res = await fetch(`${API_URL}/api/audio-cache/pre-generate-all`, {
                method: 'POST'
            });
            const data = await res.json();
            alert(`${data.message}\nThis will run in the background.`);
            setTimeout(() => fetchMeditations(), 10000);
        } catch (err) {
            alert('Error: ' + err.message);
        } finally {
            setGeneratingAudio(null);
        }
    };

    const handleUndo = () => {
        if (originalContent) {
            setEditingContent({ ...originalContent });
            setHasChanges(false);
        }
    };

    const handleSave = async (contentId) => {
        if (!editingContent) return;
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/meditations/${contentId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editingContent)
            });
            if (res.ok) {
                // Update original to match saved version
                setOriginalContent({ ...editingContent });
                setHasChanges(false);
                fetchMeditations();
                alert('Saved successfully! Changes will appear in the tool.');
            } else {
                alert('Error saving');
            }
        } catch (err) {
            alert('Error: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleStepChange = (stepIndex, field, value) => {
        const newSteps = [...editingContent.steps];
        newSteps[stepIndex] = { ...newSteps[stepIndex], [field]: value };
        setEditingContent({ ...editingContent, steps: newSteps });
    };

    const handleScriptChange = (scriptIndex, field, value) => {
        const newScript = [...editingContent.script];
        newScript[scriptIndex] = { ...newScript[scriptIndex], [field]: value };
        setEditingContent({ ...editingContent, script: newScript });
    };

    const addStep = () => {
        const newSteps = [...(editingContent.steps || []), {
            id: `step-${Date.now()}`,
            body_part: '',
            muscle_group: '',
            duration: 20,
            instruction: '',
            guidance: '',
            tension_instruction: '',
            release_instruction: ''
        }];
        setEditingContent({ ...editingContent, steps: newSteps });
    };

    const removeStep = (index) => {
        const newSteps = editingContent.steps.filter((_, i) => i !== index);
        setEditingContent({ ...editingContent, steps: newSteps });
    };

    const addScriptSegment = () => {
        const newScript = [...(editingContent.script || []), {
            text: '',
            pause: 5
        }];
        setEditingContent({ ...editingContent, script: newScript });
    };

    const removeScriptSegment = (index) => {
        const newScript = editingContent.script.filter((_, i) => i !== index);
        setEditingContent({ ...editingContent, script: newScript });
    };

    if (loading) {
        return <div className="text-center py-8 text-[#7D8E95]">Loading meditations...</div>;
    }

    const groupedMeditations = {
        body_scan: meditations.filter(m => m.content_type === 'body_scan'),
        pmr: meditations.filter(m => m.content_type === 'pmr'),
        guided_meditation: meditations.filter(m => m.content_type === 'guided_meditation')
    };

    const typeLabels = {
        body_scan: 'Body Scan',
        pmr: 'Progressive Muscle Relaxation',
        guided_meditation: 'Guided Meditations'
    };

    return (
        <div className="space-y-6">
            {/* Action Buttons */}
            <div className="flex justify-between items-center">
                <button
                    onClick={handlePreGenerateAll}
                    disabled={generatingAudio === 'all'}
                    className="flex items-center gap-2 px-4 py-2 bg-[#7D8E95]/30 text-[#7D8E95] rounded-lg hover:bg-[#7D8E95]/50 transition-colors disabled:opacity-50"
                >
                    {generatingAudio === 'all' ? (
                        <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                        <Volume2 className="w-4 h-4" />
                    )}
                    {generatingAudio === 'all' ? 'Generating...' : 'Pre-Generate All Audio'}
                </button>
                <button
                    onClick={handleSeedData}
                    disabled={seeding}
                    className="flex items-center gap-2 px-4 py-2 bg-[#7D8E95]/30 text-[#7D8E95] rounded-lg hover:bg-[#7D8E95]/50 transition-colors disabled:opacity-50"
                >
                    <RefreshCw className={`w-4 h-4 ${seeding ? 'animate-spin' : ''}`} />
                    {seeding ? 'Seeding...' : 'Seed Default Content'}
                </button>
            </div>

            {/* Content by Type */}
            {Object.entries(groupedMeditations).map(([type, items]) => (
                <div key={type} className="bg-white/80 rounded-xl p-4">
                    <h3 className="text-lg font-medium text-[#7D8E95] mb-4">{typeLabels[type]}</h3>
                    
                    {items.length === 0 ? (
                        <p className="text-sm text-[#7D8E95]">No content yet. Click "Seed Default Content" to add.</p>
                    ) : (
                        <div className="space-y-3">
                            {items.map(meditation => (
                                <div key={meditation.content_id} className="border border-[#FFBB98] rounded-lg overflow-hidden">
                                    {/* Header */}
                                    <button
                                        onClick={() => {
                                            if (expandedId === meditation.content_id) {
                                                setExpandedId(null);
                                                setEditingContent(null);
                                                setOriginalContent(null);
                                                setHasChanges(false);
                                            } else {
                                                setExpandedId(meditation.content_id);
                                                const contentCopy = JSON.parse(JSON.stringify(meditation));
                                                setEditingContent(contentCopy);
                                                setOriginalContent(JSON.parse(JSON.stringify(meditation)));
                                                setHasChanges(false);
                                            }
                                        }}
                                        className="w-full flex items-center justify-between p-3 bg-[#FBE0C3] hover:bg-[#FFBB98] transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div 
                                                className="w-3 h-3 rounded-full" 
                                                style={{ backgroundColor: meditation.color || '#7D8E95' }}
                                            />
                                            <span className="font-medium text-[#7D8E95]">{meditation.name}</span>
                                            <span className="text-xs text-[#7D8E95]">({meditation.duration})</span>
                                            {/* Cache status for guided meditations */}
                                            {type === 'guided_meditation' && cacheStatus[meditation.content_id] && (
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                    cacheStatus[meditation.content_id].fully_cached
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-amber-100 text-amber-700'
                                                }`}>
                                                    {cacheStatus[meditation.content_id].fully_cached 
                                                        ? '✓ Audio Cached' 
                                                        : `${cacheStatus[meditation.content_id].percentage}% cached`
                                                    }
                                                </span>
                                            )}
                                        </div>
                                        {expandedId === meditation.content_id ? 
                                            <ChevronUp className="w-4 h-4" /> : 
                                            <ChevronDown className="w-4 h-4" />
                                        }
                                    </button>

                                    {/* Expanded Editor */}
                                    {expandedId === meditation.content_id && editingContent && (
                                        <div className="p-4 space-y-4 bg-white">
                                            {/* Basic Info */}
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-xs text-[#7D8E95]">Name</label>
                                                    <input
                                                        type="text"
                                                        value={editingContent.name}
                                                        onChange={(e) => setEditingContent({ ...editingContent, name: e.target.value })}
                                                        className="w-full p-2 border rounded text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-[#7D8E95]">Duration</label>
                                                    <input
                                                        type="text"
                                                        value={editingContent.duration}
                                                        onChange={(e) => setEditingContent({ ...editingContent, duration: e.target.value })}
                                                        className="w-full p-2 border rounded text-sm"
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div>
                                                <label className="text-xs text-[#7D8E95]">Description</label>
                                                <textarea
                                                    value={editingContent.description}
                                                    onChange={(e) => setEditingContent({ ...editingContent, description: e.target.value })}
                                                    className="w-full p-2 border rounded text-sm"
                                                    rows={2}
                                                />
                                            </div>

                                            {/* Steps Editor (for Body Scan & PMR) */}
                                            {(type === 'body_scan' || type === 'pmr') && (
                                                <div>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <label className="text-sm font-medium text-[#7D8E95]">Steps</label>
                                                        <button
                                                            onClick={addStep}
                                                            className="flex items-center gap-1 text-xs px-2 py-1 bg-[#7D8E95]/30 rounded hover:bg-[#7D8E95]/50"
                                                        >
                                                            <Plus className="w-3 h-3" /> Add Step
                                                        </button>
                                                    </div>
                                                    <div className="space-y-3 max-h-96 overflow-y-auto">
                                                        {editingContent.steps?.map((step, idx) => (
                                                            <div key={idx} className="p-3 bg-[#FBE0C3] rounded-lg space-y-2">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-xs font-medium text-[#7D8E95]">Step {idx + 1}</span>
                                                                    <button
                                                                        onClick={() => removeStep(idx)}
                                                                        className="text-red-400 hover:text-red-600"
                                                                    >
                                                                        <Trash2 className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                                <div className="grid grid-cols-3 gap-2">
                                                                    <input
                                                                        placeholder={type === 'body_scan' ? 'Body Part' : 'Muscle Group'}
                                                                        value={type === 'body_scan' ? step.body_part || '' : step.muscle_group || ''}
                                                                        onChange={(e) => handleStepChange(idx, type === 'body_scan' ? 'body_part' : 'muscle_group', e.target.value)}
                                                                        className="p-1 text-xs border rounded"
                                                                    />
                                                                    <input
                                                                        placeholder="Duration (s)"
                                                                        type="number"
                                                                        value={step.duration}
                                                                        onChange={(e) => handleStepChange(idx, 'duration', parseInt(e.target.value))}
                                                                        className="p-1 text-xs border rounded"
                                                                    />
                                                                </div>
                                                                {type === 'pmr' && (
                                                                    <textarea
                                                                        placeholder="Tension instruction (for PMR)"
                                                                        value={step.tension_instruction || ''}
                                                                        onChange={(e) => handleStepChange(idx, 'tension_instruction', e.target.value)}
                                                                        className="w-full p-1 text-xs border rounded"
                                                                        rows={2}
                                                                    />
                                                                )}
                                                                <textarea
                                                                    placeholder={type === 'pmr' ? 'Release instruction' : 'Main instruction'}
                                                                    value={type === 'pmr' ? (step.release_instruction || '') : step.instruction}
                                                                    onChange={(e) => handleStepChange(idx, type === 'pmr' ? 'release_instruction' : 'instruction', e.target.value)}
                                                                    className="w-full p-1 text-xs border rounded"
                                                                    rows={2}
                                                                />
                                                                <textarea
                                                                    placeholder="Guidance text"
                                                                    value={step.guidance || ''}
                                                                    onChange={(e) => handleStepChange(idx, 'guidance', e.target.value)}
                                                                    className="w-full p-1 text-xs border rounded"
                                                                    rows={1}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Script Editor (for Guided Meditations) */}
                                            {type === 'guided_meditation' && (
                                                <div>
                                                    <div className="flex items-center justify-between mb-2">
                                                        <label className="text-sm font-medium text-[#7D8E95]">Script Segments</label>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handlePreGenerateAudio(meditation.content_id)}
                                                                disabled={generatingAudio === meditation.content_id}
                                                                className="flex items-center gap-1 text-xs px-2 py-1 bg-[#7D8E95]/30 rounded hover:bg-[#7D8E95]/50 disabled:opacity-50"
                                                            >
                                                                {generatingAudio === meditation.content_id ? (
                                                                    <Loader className="w-3 h-3 animate-spin" />
                                                                ) : (
                                                                    <Volume2 className="w-3 h-3" />
                                                                )}
                                                                Pre-Generate Audio
                                                            </button>
                                                            <button
                                                                onClick={addScriptSegment}
                                                                className="flex items-center gap-1 text-xs px-2 py-1 bg-[#7D8E95]/30 rounded hover:bg-[#7D8E95]/50"
                                                            >
                                                                <Plus className="w-3 h-3" /> Add Segment
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                                        {editingContent.script?.map((segment, idx) => (
                                                            <div key={idx} className="flex gap-2 items-start p-2 bg-[#FBE0C3] rounded">
                                                                <span className="text-xs text-[#7D8E95] pt-1">{idx + 1}.</span>
                                                                <textarea
                                                                    value={segment.text}
                                                                    onChange={(e) => handleScriptChange(idx, 'text', e.target.value)}
                                                                    className="flex-1 p-1 text-xs border rounded"
                                                                    rows={2}
                                                                    placeholder="What to say..."
                                                                />
                                                                <input
                                                                    type="number"
                                                                    value={segment.pause}
                                                                    onChange={(e) => handleScriptChange(idx, 'pause', parseInt(e.target.value))}
                                                                    className="w-16 p-1 text-xs border rounded"
                                                                    placeholder="Pause"
                                                                />
                                                                <button
                                                                    onClick={() => removeScriptSegment(idx)}
                                                                    className="text-red-400 hover:text-red-600 pt-1"
                                                                >
                                                                    <Trash2 className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Save and Undo Buttons */}
                                            <div className="flex justify-between items-center pt-4 border-t">
                                                <div>
                                                    {hasChanges && (
                                                        <button
                                                            onClick={handleUndo}
                                                            className="flex items-center gap-2 px-4 py-2 text-[#7D8E95] hover:text-[#7D8E95] transition-colors"
                                                        >
                                                            <Undo2 className="w-4 h-4" />
                                                            Undo Changes
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {hasChanges && (
                                                        <span className="text-xs text-amber-600">Unsaved changes</span>
                                                    )}
                                                    <button
                                                        onClick={() => handleSave(meditation.content_id)}
                                                        disabled={saving || !hasChanges}
                                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                                                            hasChanges 
                                                                ? 'bg-[#7D8E95] text-white hover:bg-[#8FB08A]' 
                                                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                        }`}
                                                    >
                                                        <Save className="w-4 h-4" />
                                                        {saving ? 'Saving...' : 'Save Changes'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default MeditationsTab;
